import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTriggerPipeline } from '../../../hooks/usePipelines.js';
import type { PipelineConfig } from '../../../types/api.js';

export interface PipelineTriggerButtonProps {
  /** トリガー対象のパイプライン設定 */
  config: PipelineConfig;
  /** 実行成功ハンドラー */
  onSuccess?: () => void;
  /** 実行エラーハンドラー */
  onError?: () => void;
  /** キャンセルハンドラー */
  onCancel?: () => void;
}

/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business パイプライン手動実行確認モーダル。確認メッセージ後にuseTriggerPipelineで実行しトースト通知。spreadsheet_sync では「末尾から読む行数」を任意で指定でき、その実行だけ深く読める（設定値は変更しない）
 * @graph-connects useTriggerPipeline [calls] パイプライン手動実行ミューテーション呼び出し
 */
export function PipelineTriggerButton({
  config,
  onSuccess,
  onError,
  onCancel,
}: PipelineTriggerButtonProps) {
  const { t } = useTranslation();
  const triggerMutation = useTriggerPipeline();
  // spreadsheet_sync だけ、その実行に限って読み取り行数を上書きできる。空なら設定値のまま。
  const canOverrideLimit = config.type === 'spreadsheet_sync';
  const [limitFromEndInput, setLimitFromEndInput] = useState('');
  const parsedLimitFromEnd = Number(limitFromEndInput);
  // 妥当性（整数・1 以上・対応する type か）の判定は API が持つ。ここでは数値でないものだけ
  // 送らないようにし、範囲や上限の条件は複製しない（複製すると API 側の変更と黙ってずれる）。
  const limitFromEnd =
    canOverrideLimit && limitFromEndInput.trim() !== '' && Number.isFinite(parsedLimitFromEnd)
      ? parsedLimitFromEnd
      : undefined;

  const handleConfirm = () => {
    triggerMutation.mutate(
      { id: config.id, ...(limitFromEnd === undefined ? {} : { limitFromEnd }) },
      {
        onSuccess: () => {
          onSuccess?.();
        },
        onError: () => {
          onError?.();
        },
      },
    );
  };

  return (
    <>
      {/* オーバーレイ */}
      <div
        className="fixed inset-0 bg-black/50 z-[var(--z-modal)]"
        onClick={onCancel}
        aria-hidden="true"
      />

      {/* 確認モーダル */}
      <div
        className="fixed inset-0 z-[var(--z-modal)] flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-label={t('pipeline.trigger')}
      >
        <div className="glass-card p-6 w-full max-w-sm animate-fade-in-up">
          {/* アイコン */}
          <div className="mx-auto w-12 h-12 rounded-full bg-[var(--color-warning-muted)] flex items-center justify-center mb-4">
            <svg
              className="w-6 h-6 text-[var(--color-warning)]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>

          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] text-center mb-2">
            {t('pipeline.trigger')}
          </h3>
          <p className="text-sm text-[var(--color-text-secondary)] text-center mb-1">
            {t('pipeline.triggerConfirm')}
          </p>
          <p className="text-sm font-medium text-[var(--color-text-primary)] text-center mb-6">
            {config.jobName}
          </p>

          {canOverrideLimit && (
            <div className="mb-6">
              <label
                htmlFor="pipeline-trigger-limit-from-end"
                className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1"
              >
                {t('pipeline.triggerLimitFromEndLabel')}
              </label>
              <input
                id="pipeline-trigger-limit-from-end"
                type="number"
                inputMode="numeric"
                value={limitFromEndInput}
                onChange={(event) => {
                  setLimitFromEndInput(event.target.value);
                }}
                disabled={triggerMutation.isPending}
                placeholder={String(config.defaultLimitFromEnd ?? '')}
                className="w-full px-3 py-2 rounded-lg text-sm bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] border border-[var(--color-border)] disabled:opacity-50"
              />
              <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                {t('pipeline.triggerLimitFromEndHint')}
              </p>
            </div>
          )}

          {/* ボタン */}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={triggerMutation.isPending}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] border border-[var(--color-border)] transition-colors disabled:opacity-50"
              aria-label={t('common.cancel')}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={triggerMutation.isPending}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-[var(--color-accent-primary)] text-white hover:bg-[var(--color-accent-hover)] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              aria-label={t('common.confirm')}
            >
              {triggerMutation.isPending && (
                <div className="loading-spinner loading-spinner-sm border-white/30 border-t-white" />
              )}
              {t('common.confirm')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
